import React, { useEffect, useState } from "react";
import { X, Mail, Sparkles, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { submitEmailLead } from "@/lib/api";

const STORAGE_KEY = "ylp_popup_shown_v1";

export default function EmailPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    let opened = false;
    const openOnce = () => {
      if (opened) return;
      opened = true;
      setOpen(true);
      localStorage.setItem(STORAGE_KEY, "1");
    };

    const timer = setTimeout(openOnce, 25000);
    const onScroll = () => {
      const scrolled =
        (window.scrollY + window.innerHeight) /
        Math.max(document.body.scrollHeight, 1);
      if (scrolled > 0.45) openOnce();
    };
    const onExit = (e) => {
      if (e.clientY < 5) openOnce();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onExit);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onExit);
    };
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Anna sähköpostiosoite.");
      return;
    }
    if (!consent) {
      toast.error("Hyväksy tietosuojaseloste jatkaaksesi.");
      return;
    }
    setLoading(true);
    try {
      await submitEmailLead(email, "email_popup");
      setDone(true);
      toast.success("Kiitos! Otamme sinuun pian yhteyttä.");
    } catch (err) {
      toast.error("Lähetys epäonnistui. Yritä uudelleen.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="email-popup"
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={close}
      />
      <div className="relative w-full max-w-md rounded-2xl border border-[#22D3EE]/30 bg-[#0D1B2A] p-8 led-glow-soft">
        <button
          onClick={close}
          data-testid="email-popup-close"
          className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors"
          aria-label="Sulje"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#22D3EE]" />
            <h3 className="mt-4 font-display text-2xl font-semibold">Kiitos!</h3>
            <p className="mt-2 text-neutral-400">
              Olemme sinuun yhteydessä yleensä saman arkipäivän aikana.
            </p>
            <button
              onClick={close}
              data-testid="email-popup-done"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-[#22D3EE] px-6 py-3 font-bold text-[#07111F] hover:bg-[#67E8F9] transition-all"
            >
              Sulje
            </button>
          </div>
        ) : (
          <>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E0B14C]/40 bg-[#E0B14C]/8 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#E0B14C]">
              <Sparkles className="h-3.5 w-3.5" /> Rajallinen määrä paikkoja
            </div>
            <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold leading-tight">
              Haluatko yhteydenoton?
            </h2>
            <p className="mt-3 text-sm text-neutral-400">
              Jätä sähköpostisi tähän — kerromme vapaana olevat mainospaikat ja
              parhaan hinnan yrityksellesi. Ei sitoumuksia.
            </p>

            <form onSubmit={submit} className="mt-5 space-y-3">
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#22D3EE]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nimi@yritys.fi"
                  data-testid="email-popup-input"
                  className="w-full rounded-md border border-[#1E2F44] bg-[#0A1729] pl-10 pr-3 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-2 focus:ring-[#22D3EE]"
                />
              </div>

              <label className="flex items-start gap-2 text-xs text-neutral-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="email-popup-consent"
                  className="mt-0.5 h-4 w-4 rounded border-[#1E2F44] bg-[#0A1729] accent-[#22D3EE]"
                />
                <span>
                  Hyväksyn, että yhteystietojani käytetään yhteydenottoa varten.
                  Lue{" "}
                  <Link
                    to="/tietosuoja"
                    onClick={close}
                    className="text-[#22D3EE] hover:text-[#67E8F9] underline"
                  >
                    tietosuojaseloste
                  </Link>
                  .
                </span>
              </label>

              <button
                type="submit"
                disabled={loading}
                data-testid="email-popup-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-[#E0B14C] to-[#F1C46A] px-5 py-3 font-bold text-[#1A1206] hover:brightness-110 transition-all shadow-md shadow-[#E0B14C]/30 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Lähetetään…
                  </>
                ) : (
                  <>
                    Ota yhteyttä <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={close}
                data-testid="email-popup-dismiss"
                className="w-full text-xs text-neutral-500 hover:text-neutral-300 transition-colors"
              >
                Ei kiitos, katson vielä
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
